import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, MessageSquare, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { fmtUSD, relativeTime } from "@/lib/utils";

export const PIPELINE_STAGES = [
  { key: "new_interest", label: "New Interest", color: "bg-sky-50 text-sky-700 border-sky-200" },
  { key: "contact_attempted", label: "Contact Attempted", color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  { key: "contact_made", label: "Contact Made", color: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "meeting_scheduled", label: "Meeting Scheduled", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "negotiating", label: "Negotiating", color: "bg-orange-50 text-orange-700 border-orange-200" },
  { key: "invested", label: "Invested", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "not_proceeding", label: "Not Proceeding", color: "bg-neutral-900 text-white border-neutral-900" },
];

export function pipelineStage(key) {
  return PIPELINE_STAGES.find((s) => s.key === key) || PIPELINE_STAGES[0];
}

export default function DeveloperLeads() {
  const [interests, setInterests] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "all";

  useEffect(() => { load(); }, []);
  async function load() {
    const { data } = await api.get("/interests/mine");
    setInterests(data);
  }

  const filtered = useMemo(() => {
    return interests.filter((i) => filter === "all" || i.status === filter);
  }, [interests, filter]);

  async function updateStatus(convoId, next, i) {
    if (!convoId) {
      toast.error("This lead has no conversation attached yet.");
      return;
    }
    try {
      await api.patch(`/conversations/${convoId}/status`, { status: next });
      toast.success(`Marked ${pipelineStage(next).label}`);
      await load();
    } catch {
      toast.error("Could not update stage");
    }
  }

  function setFilter(f) {
    if (f === "all") searchParams.delete("filter");
    else searchParams.set("filter", f);
    setSearchParams(searchParams);
  }

  return (
    <div className="max-w-7xl mx-auto">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Leads</p>
      <h1 className="text-4xl font-bold text-neutral-900 mt-2">Investor pipeline</h1>
      <p className="text-neutral-500 mt-2">Every interest opens a chat thread. Move each lead through the pipeline as you engage.</p>

      <div className="flex gap-2 mt-8 flex-wrap">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="leads-filter-all">All ({interests.length})</FilterChip>
        {PIPELINE_STAGES.map((s) => {
          const count = interests.filter((i) => i.status === s.key).length;
          return (
            <FilterChip key={s.key} active={filter === s.key} onClick={() => setFilter(s.key)} testId={`leads-filter-${s.key}`}>
              {s.label} ({count})
            </FilterChip>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-neutral-200 bg-white p-12 text-center mt-10">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
            <Users size={22} />
          </div>
          <p className="text-lg font-semibold text-neutral-900">No leads in this stage</p>
          <p className="text-sm text-neutral-500 mt-1">Try a different filter or check All.</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden mt-8 space-y-3">
            {filtered.map((i) => (
              <div key={i.id} data-testid={`lead-card-${i.id}`} className="soft-card p-4">
                <div className="flex items-center gap-3">
                  <Link to={`/u/${i.investor?.id}`} data-testid={`lead-investor-link-${i.id}`}>
                    <Avatar name={i.investor?.name} size={40} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/u/${i.investor?.id}`} className="font-semibold text-neutral-900 truncate block hover:text-propera">{i.investor?.name}</Link>
                    <p className="text-xs text-neutral-500 truncate">{i.investor?.email}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-100 text-sm space-y-1">
                  <p className="text-neutral-700"><span className="text-xs text-neutral-500">Development:</span> {i.development?.name}</p>
                  <p className="text-neutral-700"><span className="text-xs text-neutral-500">Unit:</span> {i.unit ? `${i.unit.label} · ${i.unit.bedrooms} · ${fmtUSD(i.unit.price)}` : "Any unit"}</p>
                  <p className="text-[11px] text-neutral-400">{relativeTime(i.created_at)}</p>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <StagePicker current={i.status} onChange={(s) => updateStatus(i.conversation_id, s, i)} />
                  {i.conversation_id && (
                    <Link to={`/messages?c=${i.conversation_id}`} data-testid={`lead-open-chat-${i.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-propera">
                      <MessageSquare size={14} /> Open chat
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block mt-8 soft-card overflow-visible">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
                  <th className="text-left px-6 py-4 font-semibold">Investor</th>
                  <th className="text-left px-6 py-4 font-semibold">Development</th>
                  <th className="text-left px-6 py-4 font-semibold">Unit</th>
                  <th className="text-left px-6 py-4 font-semibold">Submitted</th>
                  <th className="text-left px-6 py-4 font-semibold">Pipeline Stage</th>
                  <th className="text-left px-6 py-4 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} className="border-t border-neutral-100 hover:bg-neutral-50/50 transition" data-testid={`lead-${i.id}`}>
                    <td className="px-6 py-4">
                      <Link to={`/u/${i.investor?.id}`} data-testid={`lead-investor-link-${i.id}`} className="flex items-center gap-3 group">
                        <Avatar name={i.investor?.name} size={36} />
                        <div>
                          <p className="font-semibold text-neutral-900 group-hover:text-propera">{i.investor?.name}</p>
                          <p className="text-xs text-neutral-500">{i.investor?.email}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-neutral-700">{i.development?.name}</td>
                    <td className="px-6 py-4 text-neutral-700">{i.unit ? `${i.unit.label} · ${i.unit.bedrooms} · ${fmtUSD(i.unit.price)}` : "Any unit"}</td>
                    <td className="px-6 py-4 text-xs text-neutral-500">{relativeTime(i.created_at)}</td>
                    <td className="px-6 py-4">
                      <StagePicker current={i.status} onChange={(s) => updateStatus(i.conversation_id, s, i)} />
                    </td>
                    <td className="px-6 py-4">
                      {i.conversation_id ? (
                        <Link to={`/messages?c=${i.conversation_id}`} data-testid={`lead-open-chat-${i.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-propera">
                          <MessageSquare size={14} /> Open chat
                        </Link>
                      ) : (
                        <span className="text-xs text-neutral-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`px-4 py-2 rounded-full text-sm font-medium border transition ${active ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>
      {children}
    </button>
  );
}

export function StagePicker({ current, onChange, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const s = pipelineStage(current || "new_interest");
  const px = size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs";
  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-testid="stage-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold border ${s.color} ${px} hover:opacity-90`}
      >
        {s.label} <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 left-0 mt-2 w-52 rounded-2xl border border-neutral-200 bg-white shadow-lg p-1">
            {PIPELINE_STAGES.map((opt) => (
              <button
                key={opt.key}
                data-testid={`stage-option-${opt.key}`}
                onClick={() => { onChange(opt.key); setOpen(false); }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm hover:bg-neutral-50"
              >
                <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold border px-2 py-0.5 text-[10px] ${opt.color}`}>
                  {opt.label}
                </span>
                {opt.key === current && <Check size={14} className="text-propera" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
